import { useAuth0 } from "@auth0/auth0-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { MemberAttribute, User } from "../types/dbTypes";
import { apiRequest } from "../api";
import { clearCookies } from "../utils/Cookie";
import { isPublicRoute } from "../config/publicRoutes";

interface IAccountProviderProps {
  appName: string;
}

export const useAccountProvider = ({ appName }: IAccountProviderProps) => {
  const [latestToken, setLatestToken] = useState<string>(null);
  const [isUpdatingCompany, setIsUpdatingCompany] = useState<boolean>(false);

  const [vojomoAccount, setVojomoAccount] = useState<User>(
    localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null
  );

  const [memberAccount, setMemberAccount] = useState<MemberAttribute>(
    localStorage.getItem("member")
      ? JSON.parse(localStorage.getItem("member"))
      : null
  );

  const companyName = useMemo(() => {
    return vojomoAccount?.impersonated_company || vojomoAccount?.company;
  }, [vojomoAccount]);

  const [userUnauthorized, setUserUnauthorized] = useState<boolean>(false);

  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
    user,
    getAccessTokenSilently
  } = useAuth0();

  const login = (goToHomepage: boolean = false) => {
    // After an explicit fullLogout, force the Auth0 login UI even if the
    // upstream IdP (e.g. Google) still has a live session. Without this,
    // silent SSO will sign the user right back in and make logout look
    // like a no-op.
    const forcePrompt =
      localStorage.getItem("vojomo_force_login_prompt") === "1";
    if (forcePrompt) {
      localStorage.removeItem("vojomo_force_login_prompt");
    }
    loginWithRedirect({
      appState: {
        returnTo: goToHomepage ? window.location.origin : window.location.href
      },
      authorizationParams: forcePrompt ? { prompt: "login" } : undefined
    });
  };

  const verifyUser = async () => {
    try {
      const token = await getAccessTokenSilently();
      setLatestToken(token);

      const url = `${import.meta.env.VITE_REACT_APP_SERVER}/auth?app_name=${appName}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Email: user.email
        },
        credentials: "include"
      });

      let userData: unknown;
      try {
        userData = await response.json();
      } catch {
        setUserUnauthorized(true);
        return;
      }

      if (response.status === 401 || response.status === 403) {
        setUserUnauthorized(true);
      } else if (response.ok) {
        setVojomoAccount(userData as User);
      } else {
        setUserUnauthorized(true);
      }
    } catch {
      // getAccessTokenSilently failed — Auth0 session expired; redirect to login
      login();
    }
  };

  useEffect(() => {
    if (vojomoAccount) {
      localStorage.setItem("user", JSON.stringify(vojomoAccount));
    } else {
      localStorage.removeItem("user");
    }
  }, [vojomoAccount]);

  useEffect(() => {
    if (vojomoAccount) {
      const clientTimeZone =
        Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;

      if (clientTimeZone && clientTimeZone !== vojomoAccount.timezone) {
        apiRequest<MemberAttribute>({
          method: "POST",
          subRoute: "user",
          path: "/update_timezone",
          body: { timezone: clientTimeZone }
        }).then(() => {
          setVojomoAccount((prev) => {
            const newUser = {
              ...prev,
              timezone: clientTimeZone
            };
            return newUser;
          });
        });
      }
    }
  }, [vojomoAccount]);

  useEffect(() => {
    if (vojomoAccount) {
      apiRequest<MemberAttribute>({
        method: "GET",
        subRoute: "user",
        path: `/member_attributes`
      }).then((memberAttribute) => {
        setMemberAccount(memberAttribute);
      });
    }
  }, [vojomoAccount]);

  useEffect(() => {
    if (memberAccount) {
      localStorage.setItem("member", JSON.stringify(memberAccount));
    } else {
      localStorage.removeItem("member");
    }
  }, [memberAccount]);

  useEffect(() => {
    // Public routes that don't require authentication. Source of truth is
    // shared/src/config/publicRoutes.ts, used here AND by App.tsx to render
    // the matching public components. Keep both consumers in sync via that
    // shared list — never hand-extend a copy here.
    const onPublicRoute = isPublicRoute(window.location.pathname);

    if (isAuthenticated) {
      verifyUser();
    } else if (!isLoading && !onPublicRoute) {
      login();
    }
  }, [isAuthenticated, isLoading]);

  const fullLogout = async () => {
    // Server-side cookie clear. `auth0_user_token` is set with httpOnly=true,
    // so JS / clearCookies() can't expire it — only the server can. Without
    // this call, the cookie persists for 14 days after Auth0 logout and
    // backend calls keep succeeding, masking the half-logged-out state.
    try {
      await fetch(`${import.meta.env.VITE_REACT_APP_SERVER}/logout`, {
        method: "POST",
        credentials: "include"
      });
    } catch {
      // Network errors here are non-fatal — Auth0 logout still runs below.
    }
    localStorage.clear();
    clearCookies();
    // Marker for the post-logout login() call to force the Auth0 login UI
    // even if the upstream IdP (Google) still has a live session and silent
    // SSO would otherwise re-authenticate the user instantly. Set AFTER
    // localStorage.clear() so it survives the reload.
    localStorage.setItem("vojomo_force_login_prompt", "1");
    // `federated: true` tells Auth0 to also hit the upstream IdP's logout
    // endpoint. Belt-and-suspenders with the force-prompt flag — Google
    // often ignores third-party logout requests, but Auth0 still cleans
    // its own SSO cookie more thoroughly when this is set.
    await logout({
      logoutParams: { returnTo: window.location.origin, federated: true }
    });
  };

  const triggerGoogleOauth = useCallback(
    async (state: string) => {
      localStorage.removeItem("google_oauth");

      //Google's OAuth 2.0 endpoint for requesting an access token
      let oauth2Endpoint =
        "https://accounts.google.com/o/oauth2/v2/auth?access_type=offline";

      // Create <form> element to submit parameters to OAuth 2.0 endpoint.
      let form = document.createElement("form");
      form.setAttribute("method", "GET"); // Send as a GET request.
      form.setAttribute("action", oauth2Endpoint);

      // Parameters to pass to OAuth 2.0 endpoint.
      let params = {
        client_id: import.meta.env.VITE_REACT_APP_CLIENT_ID, // process.env.REACT_APP_CLIENT_ID,
        redirect_uri: `${window.location.origin}/google-callback`,
        response_type: "token",
        scope: "https://www.googleapis.com/auth/drive.file",
        prompt: "consent",
        include_granted_scopes: "false",
        state
      };

      if (!!user) {
        params["login_hint"] = user.email;
      }

      // Add form parameters as hidden input values.
      for (let p in params) {
        let input = document.createElement("input");
        input.setAttribute("type", "hidden");
        input.setAttribute("name", p);
        input.setAttribute("value", params[p]);
        form.appendChild(input);
      }

      // Add form to page and submit it to open the OAuth 2.0 endpoint.
      document.body.appendChild(form);
      form.submit();
    },
    [user]
  );

  const updateImpersonatedCompany = async (impersonatedCompany: string) => {
    setIsUpdatingCompany(true);
    await apiRequest({
      method: "POST",
      subRoute: "user",
      path: "/update_impersonated_company",
      body: { company_name: impersonatedCompany }
    });
    setIsUpdatingCompany(false);

    setVojomoAccount((prev) => {
      return { ...prev, impersonated_company: impersonatedCompany };
    });
  };

  return {
    vojomoAccount,
    setVojomoAccount,
    userUnauthorized,
    companyName,

    updateImpersonatedCompany,

    triggerGoogleOauth,

    latestToken,

    memberAccount,

    isUpdatingCompany,

    login,
    fullLogout
  };
};

export const AccountProvider = ({ appName, children }) => {
  const accountProvider = useAccountProvider({ appName });

  return (
    <AccountContext.Provider value={accountProvider}>
      {children}
    </AccountContext.Provider>
  );
};

export type AccountProps = ReturnType<typeof useAccountProvider>;
export const AccountContext = createContext<AccountProps>(null);
export const useAccount = () => useContext(AccountContext);
